/**
 * Ranks a row joining a bundle, trying each anchor in turn and taking the first
 * rank no other row can already hold. Consumed only by
 * `useArTableActions.moveToBundle`, which offers the aimed drop position, then
 * the bundle's last on-page member, then the header — so an unusable anchor falls
 * through to the next instead of abandoning the move (SYD-2323). Soundness rests
 * on `orderedRowIds` being one Firestore window in rank order, which holds on the
 * drag paths (`viewSort.mode === 'order'`) but NOT on the context-menu paths,
 * where a column sort reorders the window. The rendered list must never supply
 * bounds: it hides collapsed members whose ranks fill the gap (SYD-2186). With no
 * sound interval this returns `null` and the row joins without moving.
 */

import { allocateRowOrders } from './allocateRowOrders';
import type { ArRowId } from '#types';

type ArBundleJoinAnchor = {
  rowId: ArRowId;
  /**
   * May the joining row take the slot immediately BEFORE this anchor? False for
   * a bundle header — a member ranked above its own header renders outside the
   * block, which is the symptom the join exists to remove.
   */
  mayPrecede: boolean;
};

type AllocateMoveToBundleOrderParams = {
  anchors: readonly ArBundleJoinAnchor[];
  orderedRowIds: readonly ArRowId[];
  getOrder: (rowId: ArRowId) => string | undefined;
  pageDocCount: number;
  pageSize: number;
  currentPage: number;
  totalPages: number;
};

/**
 * Three ways to rank against one anchor, in descending order of certainty:
 * bisect the open interval up to the next doc on the page, which no row occupies;
 * append with `genNext()` when this page ends the collection; or bisect the
 * interval before the anchor, which `mayPrecede` forbids for a header.
 *
 * "Ends the collection" is an assumption, not a proof. An unfilled `limit` is
 * solid, but `totalPages` comes from a count that only re-fires when the local
 * window's length changes, so a cross-client append can leave it stale-low
 * indefinitely — and past a genuinely non-final tail `genNext()` reproduces the
 * rank the next page's first row already holds.
 */
function allocateAfterAnchor(
  anchor: ArBundleJoinAnchor,
  {
    orderedRowIds,
    getOrder,
    pageDocCount,
    pageSize,
    currentPage,
    totalPages,
  }: AllocateMoveToBundleOrderParams,
): string | null {
  const anchorOrder = getOrder(anchor.rowId);
  if (anchorOrder === undefined || anchorOrder.length === 0) return null;

  const anchorIndex = orderedRowIds.indexOf(anchor.rowId);
  if (anchorIndex < 0) return null;

  const nextId = orderedRowIds[anchorIndex + 1];
  const nextOrder = nextId !== undefined ? getOrder(nextId) : undefined;
  if (nextOrder !== undefined && nextOrder.length > 0) {
    return allocateRowOrders({ floorOrder: anchorOrder, ceilingOrder: nextOrder, count: 1 })[0];
  }

  const pageEndsCollection = pageDocCount < pageSize
    || (totalPages > 0 && currentPage >= totalPages);
  if (pageEndsCollection) {
    return allocateRowOrders({ floorOrder: anchorOrder, ceilingOrder: null, count: 1 })[0];
  }

  if (!anchor.mayPrecede) return null;

  const prevId = anchorIndex > 0 ? orderedRowIds[anchorIndex - 1] : undefined;
  const prevOrder = prevId !== undefined ? getOrder(prevId) : undefined;
  if (prevOrder !== undefined && prevOrder.length > 0 && prevOrder < anchorOrder) {
    return allocateRowOrders({ floorOrder: prevOrder, ceilingOrder: anchorOrder, count: 1 })[0];
  }

  return null;
}

export function allocateMoveToBundleOrder(params: AllocateMoveToBundleOrderParams): string | null {
  return params.anchors.reduce<string | null>(
    (rank, anchor) => rank ?? allocateAfterAnchor(anchor, params),
    null,
  );
}
